import React, { useEffect, useMemo, useState } from "react";
import { Calendar, FileText, Inbox, Package, Play, Sparkles } from "lucide-react";
import TheNav from "../Components/navbar";
import { useParams } from "react-router-dom";
import { auth, db } from "../Context/firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import { loadStripe } from "@stripe/stripe-js";
import OrderActions from "../Components/shared/OrderActions";
import SEO from "../Components/SEO";
import { trackEvent } from "../utils/analytics";
import {
  ErrorCard,
  FieldRow,
  GhostAction,
  GlassCard,
  MetricCard,
  OrderTabs,
  PrimaryAction,
  SkeletonBlock,
  StatusBadge,
  MINT,
  PURPLE,
} from "../Components/order/OrderPrimitives";

const stripePromise = loadStripe(`${process.env.REACT_APP_STRIPE_PUBLIC_KEY}`);

export default function OrderDisplay() {
  const { orderNumber } = useParams();
  const [orderData, setOrderData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchaseTracked, setPurchaseTracked] = useState(false);
  const [activeTab, setActiveTab] = useState("details");

  useEffect(() => {
    const fetchOrderData = async () => {
      try {
        setLoading(true);
        setError(null);

        const currentUserId = auth.currentUser ? auth.currentUser.uid : null;

        if (!currentUserId) {
          setError("User is not authenticated");
          setLoading(false);
          return;
        }

        const userDocRef = doc(db, "Users", currentUserId);
        const docSnap = await getDoc(userDocRef);

        if (!docSnap.exists()) {
          setError("User document not found");
          setLoading(false);
          return;
        }

        const orders = docSnap.data().orders || {};
        const foundOrder =
          orders.pending?.[orderNumber] ??
          orders.current?.[orderNumber] ??
          orders.previous?.[orderNumber];

        if (foundOrder) {
          setOrderData(foundOrder);
        } else {
          setError("Order not found");
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching order data:", err);
        setError("Failed to fetch order data");
        setLoading(false);
      }
    };

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        fetchOrderData();
      } else {
        setError("User is not authenticated");
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [orderNumber]);

  useEffect(() => {
    if (orderData?.status === "current" && !purchaseTracked) {
      trackEvent("purchase", {
        value: orderData.price?.final,
        currency: "USD",
        orderId: orderNumber,
      });
      setPurchaseTracked(true);
    }
  }, [orderData, orderNumber, purchaseTracked]);

  const handleCheckout = async () => {
    try {
      trackEvent("stripe_checkout_opened");
      const stripe = await stripePromise;
      const response = await fetch(
        `${process.env.REACT_APP_ENDPOINT}/create-checkout-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            items: [
              {
                id: orderNumber,
                title: orderData.title,
                price: orderData.price.final,
                quantity: 1,
                creatorId: orderData.creatorId,
                editorId: orderData.editorId,
              },
            ],
          }),
        }
      );

      const data = await response.json();
      if (data.id) {
        const { error } = await stripe.redirectToCheckout({
          sessionId: data.id,
        });
        if (error) {
          console.error("Stripe checkout error:", error);
        }
      } else {
        console.error("Failed to create checkout session");
      }
    } catch (error) {
      console.error("Error during checkout:", error);
    }
  };

  const action = useMemo(() => {
    if (!orderData || !auth.currentUser) return null;

    if (
      orderData.status === "pending" &&
      orderData.creatorId === auth.currentUser.uid &&
      orderData.files == null
    ) {
      return {
        label: "Begin Order",
        icon: Play,
        onClick: () => {
          trackEvent("begin_order", {
            creatorId: auth.currentUser.uid,
          });
          handleCheckout();
        },
      };
    }

    if (orderData.status === "pending" && orderData.files != null) {
      return { label: "Waiting for Acceptance", icon: Inbox, disabled: true };
    }

    if (orderData.status === "progress") {
      return { label: "In Progress", icon: Sparkles, disabled: true };
    }

    if (orderData.status === "current") {
      return { label: "Current", icon: Sparkles, disabled: true };
    }

    return null;
  }, [orderData]);

  const tabs = [
    { id: "details", label: "Details", icon: FileText },
    { id: "delivery", label: "Delivery", icon: Package },
  ];

  return (
    <>
      <SEO
        title="Order Details | Kre8"
        description="Private Kre8 order details for creators and editors."
        canonicalPath={`/order/${orderNumber}`}
        noindex
      />
      <TheNav />

      <main className="relative min-h-screen overflow-hidden bg-[#07060d] text-white">
        <div
          className="pointer-events-none absolute -top-32 left-1/2 h-[420px] w-[820px] -translate-x-1/2 rounded-full opacity-25 blur-[140px]"
          style={{ background: `radial-gradient(circle, ${PURPLE}, transparent 60%)` }}
        />
        <div
          className="pointer-events-none absolute right-0 top-40 h-[380px] w-[520px] rounded-full opacity-20 blur-[120px]"
          style={{ background: `radial-gradient(circle, ${MINT}, transparent 60%)` }}
        />

        <div className="relative mx-auto max-w-[1200px] px-4 py-6 sm:px-6 lg:px-8">
          {loading ? (
            <div className="space-y-4">
              <SkeletonBlock className="h-32" />
              <SkeletonBlock className="h-12" />
              <SkeletonBlock className="h-64" />
            </div>
          ) : error ? (
            <ErrorCard message={error} />
          ) : orderData ? (
            <>
              <GlassCard glow="purple" className="mb-5">
                <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-white/40">
                        Order
                      </span>
                      <span className="font-mono text-[11px] text-white/40">
                        #{orderNumber}
                      </span>
                    </div>
                    <h1 className="mt-1.5 text-xl font-bold tracking-tight text-white sm:text-2xl">
                      {orderData.title}
                    </h1>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <StatusBadge status={orderData.status} />
                      {orderData.dueDate && (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-white/60">
                          <Calendar size={11} /> Due {orderData.dueDate}
                        </span>
                      )}
                    </div>
                  </div>

                  {action && (
                    <div className="shrink-0">
                      {action.disabled ? (
                        <GhostAction disabled>
                          {action.icon && <action.icon size={14} />}
                          {action.label}
                        </GhostAction>
                      ) : (
                        <PrimaryAction onClick={action.onClick}>
                          {action.icon && <action.icon size={14} />}
                          {action.label}
                        </PrimaryAction>
                      )}
                    </div>
                  )}
                </div>
              </GlassCard>

              <div className="mb-5">
                <OrderTabs tabs={tabs} value={activeTab} onChange={setActiveTab} />
              </div>

              {activeTab === "details" && (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <MetricCard
                      label="Initial"
                      value={`$${orderData.price?.initial ?? "-"}`}
                      accent="purple"
                    />
                    <MetricCard
                      label="Fees"
                      value={`$${orderData.price?.fees ?? "-"}`}
                      accent="purple"
                    />
                    <MetricCard
                      label="Final"
                      value={`$${orderData.price?.final ?? "-"}`}
                      accent="mint"
                    />
                  </div>

                  <GlassCard glow="purple">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-white/40">
                      Description
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-white/80">
                      {orderData.description}
                    </p>
                  </GlassCard>

                  <GlassCard glow="mint">
                    <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-white/40">
                      Order info
                    </h3>
                    <FieldRow label="Order Title" value={orderData.title} />
                    <FieldRow label="Due Date" value={orderData.dueDate || "-"} />
                    <FieldRow
                      label="Status"
                      value={<StatusBadge status={orderData.status} />}
                    />
                    <FieldRow label="Order Number" value={`#${orderNumber}`} mono />
                  </GlassCard>
                </div>
              )}

              {activeTab === "delivery" && (
                <GlassCard glow="mint">
                  <OrderActions orderData={orderData} orderNumber={orderNumber} />
                </GlassCard>
              )}
            </>
          ) : null}
        </div>
      </main>
    </>
  );
}
